import { getLocale } from '@edx/frontend-platform/i18n';

import { DEFAULT_LANGUAGE, ALLOWED_SITE_LANGUAGES } from './languageSwitcher';

/**
 * A map from frontend-platform locale codes to statically imported image URLs.
 *
 * Keys must be valid locale codes recognised by the site (e.g. 'en', 'es-419',
 * 'fr', 'hi', 'sw', 'zh-cn'). Values are webpack-resolved URL strings produced
 * by importing image assets (PNG, SVG, JPEG, etc.) at module level.
 *
 * The map must always contain an entry for the DEFAULT_LANGUAGE ('en'), which
 * acts as the fallback when the current locale has no dedicated image.
 *
 * @example
 * import { img-en, img-fr } from '../../img';
 *
 * const bannerImages: LocalizedImageMap = {
 *   en: img-en,
 *   fr: img-fr,
 * };
 */
export type LocalizedImageMap = Partial<Record<string, string>> & { en: string };

/**
 * Returns the image URL that corresponds to the current frontend-platform locale.
 *
 * Resolution order:
 * 1. If the current locale is in ALLOWED_SITE_LANGUAGES AND present in imageMap,
 *    return imageMap[locale].
 * 2. If the current locale is unknown or absent from imageMap, return imageMap['en'].
 *
 * @param {LocalizedImageMap} imageMap - A map of locale codes to image URL strings.
 *   Must include an 'en' entry as the default fallback.
 * @returns {string} The image URL for the active locale, or the English fallback.
 *
 * @example
 * const src = getLocalizedImage(bannerImages);
 * // Returns the French SVG when the site locale is 'fr',
 * // or the English SVG for any locale not present in the map.
 */
export function getLocalizedImage(imageMap: LocalizedImageMap): string {
  const locale = ALLOWED_SITE_LANGUAGES.includes(getLocale()) ? getLocale() : DEFAULT_LANGUAGE;
  return imageMap[locale] ?? imageMap[DEFAULT_LANGUAGE];
}
